import { useCallback, useState } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

// importing custom components
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../components/Dialog";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import { Button, LabelBox } from "../../components";
import { updateStatusSchema } from "../../yup";
import Input from "../../components/UserInput/Input";

// css imports and images
import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import style from "./heathOrganisation.module.css";
import { colors } from "../../theme/colors";
// importing the api services
import {
  useGetHealthOrganisationsQuery,
  useUpdateHealthOrganisationDetailsMutation,
} from "../../services/api";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import Logs from "../../assets/images/logs.png";

type FormState = typeof updateStatusSchema.__outputType;
type HealthOrganisationAction =
  | "view"
  | "edit"
  | "logs"
  | "deactivate"
  | "activate";

// Functional component representing the Health Organisation page
const HealthOrganisations = () => {
  const [updateHealthOrganisationDetails] = useUpdateHealthOrganisationDetailsMutation();
  const navigate = useNavigate();

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: ""
  });
  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedHealthOrganisation, setSelectedHealthOrganisation] = useState<HealthOrganisation | null>(
    null
  );
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false)

  const { data: healthOrganisationData, isFetching } = useGetHealthOrganisationsQuery(queryParam);

  // for handling the health organisation state
  const handleMenu = (value: HealthOrganisationAction, item: HealthOrganisation) => {
    setSelectedHealthOrganisation(item);
    if (value === "deactivate") {
      setModalVisible(true);
    } else if (value === "activate") {
      setModalVisible(true);
    } else if (value === "view") {
      navigate(`/health-organisations/details/${item?.id}`);
    } else if (value === "edit") {
      navigate(`/health-organisations/edit/${item?.id}`);
    } else if (value === "logs") {
      setLogModalVisible(true);
    }
  };

  // update the fields
  const fields = [
    { id: "name", label: "Health Organisation Name", flex: 2, sortable: true },
    {
      id: "street_address",
      label: "Address",
      type: "address",
      flex: 2.5,
      render: (item: HealthOrganisation) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item.address?.unit_number ?? ""} ${item.address?.street_number ?? ""} ${item.address?.street_name ?? ""}, ${item?.address?.suburb ?? ""} ${item?.address?.state ?? ""
            } ${item?.address?.postcode ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "phone",
      label: "Phone Number",
      type: "phone_number",
      render: (item: HealthOrganisation) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item.address?.phone ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      flex: 0.8,
      sortable: true,
      render: (item: HealthOrganisation) => <StatusBadge isActive={item.is_active} />,
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      render: (item: HealthOrganisation) => (
        <CustomActionMenu<HealthOrganisation, HealthOrganisationAction>
          item={item}
          isActive={item.is_active}
          handleChange={handleMenu}
          actions={
            [
              {
                label: "View",
                value: "view",
                icon: <EyeIcon height={24} width={24} />,
              },
              ...(item.is_active
                ? ([
                  {
                    label: "Edit",
                    value: "edit",
                    icon: <EditIcon height={24} width={24} />,
                  },
                ] as ActionItem<HealthOrganisationAction>[])
                : []),
              {
                label: "Logs",
                value: "logs",
                icon: <img src={Logs} alt="Logs" width={24} />,
              },
              {
                label: item.is_active ? "Deactivate" : "Activate",
                value: item.is_active ? "deactivate" : "activate",
                icon: item.is_active ? (
                  <DeactivateIcon height={24} width={24} />
                ) : (
                  <PowerSettingsNewIcon height={24} width={24} />
                ),
              },
            ] as ActionItem<HealthOrganisationAction>[]
          }
        />
      ),
    },
  ];

  // for searching in the query
  const handleSearch = useCallback(
    (e: any) => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: e.target.value,
      }));
    },
    [setQueryParam]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  const onSubmit = async (values: FormState) => {
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      await updateHealthOrganisationDetails({
        is_active: !selectedHealthOrganisation?.is_active,
        id: selectedHealthOrganisation?.id ?? 0,
        deactivate_reason,
      }).unwrap();
      setIsLoading(false);
      setReasonModalVisible(false);
      setValue("deactivate_reason", "");
      clearErrors();
    } catch (error: any) {
      setIsLoading(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    }
  };

  // Render the Health Organisation component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={queryParam.search_key}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Link to="add">
          <Button
            variant="contained"
            className={style.addButton}
            label="Add New Health Organisation"
            sx={{
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Link>
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<HealthOrganisation>
          fields={fields}
          paginationRequired={true}
          data={healthOrganisationData?.items || []}
          loading={isFetching}
          onDoubleClick={(hospital: HealthOrganisation) => {
            navigate(`/health-organisations/details/${hospital?.id}`);
          }}
          defaultOrderBy="name"
          searchTerm={queryParam.search_key}
          onClearSearch={() => setQueryParam((prev) => ({ ...prev, search_key: "" }))}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to ${selectedHealthOrganisation?.is_active ? "deactivate " : "activate "
            }"${selectedHealthOrganisation?.name}"?`}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={() => {
            setModalVisible(false);
            if (selectedHealthOrganisation?.is_active) {
              setReasonModalVisible(true);
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonModalVisible && (
        <InputDialog
          visible={reasonModalVisible}
          onClose={() => {
            setReasonModalVisible(false);
            setValue("deactivate_reason", "");
            clearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  setReasonModalVisible(false);
                  setValue("deactivate_reason", "");
                  clearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={selectedHealthOrganisation}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
}
export default HealthOrganisations;
